import { has_joined } from "./stores.js";
import { OutputConfig, BuildSettings } from "./OutputConfig.js";
import { Logger } from "./Logger.js";

/*
 Keep a phone's screen on while in a session. Chrome only takes this lock by itself for a
 playing <video>, and the program is a canvas, so phones dimmed and locked mid-session; a locked
 Android phone then loses the relay (Android 15 blocks a backgrounded app's network).

 Held while joined and visible. The browser releases it whenever the page is hidden, so it is
 requested again on every return to visible.
*/

const MAX_FAILURE_LOGS = 5;

export const ScreenWakeLock = {

    _joined: false,
    _sentinel: null,
    _pending: false,
    _acquired_at: 0,
    _failures: 0,
    _unsupported_logged: false,

    init(){
        document.addEventListener("visibilitychange", () => this._apply("visible"));
        has_joined.subscribe(joined => {
            this._joined = joined;
            this._apply("join");
        });
    },

    _wanted(){
        return BuildSettings.MOBILE_SCREEN_WAKE_LOCK
            && OutputConfig.session?.browser?.isMobile === true
            && this._joined
            && document.visibilityState === "visible";
    },

    async _apply(trigger){

        if (!this._wanted() || this._sentinel || this._pending)
            return;

        if (!navigator.wakeLock){
            if (!this._unsupported_logged){
                this._unsupported_logged = true;
                Logger.log("Page Load", "info", "Wake Lock Unsupported", {});
            }
            return;
        }

        this._pending = true;
        try {
            const sentinel = await navigator.wakeLock.request("screen");
            this._sentinel = sentinel;
            this._acquired_at = performance.now();
            Logger.log("Page Load", "info", "Wake Lock Acquired", {trigger});
            sentinel.addEventListener("release", () => this._onRelease(sentinel));
            // -- hidden again while the request was in flight: it may already be gone
            if (sentinel.released) this._onRelease(sentinel);
        } catch(e) {
            if (this._failures++ < MAX_FAILURE_LOGS)
                Logger.log("Page Load", "warn", "Wake Lock Failed", {trigger, error: `${e?.name}: ${e?.message}`});
        } finally {
            this._pending = false;
        }
    },

    _onRelease(sentinel){

        if (this._sentinel !== sentinel)
            return;

        this._sentinel = null;
        Logger.log("Page Load", "info", "Wake Lock Released", {
            by: document.visibilityState === "hidden" ? "page-hidden" : "system",
            held_s: Math.round((performance.now() - this._acquired_at) / 1000),
        });
    },
};
